"use client";

import { ReactNode, useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";

/**
 * Hero section that shrinks, fades, and moves away as user scrolls past.
 * Creates a "scrolling past a scene" cinematic effect.
 *
 * Trên mobile, hiệu ứng scroll được làm nhẹ lại (không fade/scale theo cuộn)
 * để tránh cảm giác giật, đặc biệt quanh trình phát nhạc.
 */
interface HeroScrollSceneProps {
    children: ReactNode;
    className?: string;
}

export function HeroScrollScene({ children, className }: HeroScrollSceneProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (typeof window === "undefined") return;
            setIsMobile(window.innerWidth < 768);
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });

    // Khi cuộn xuống, hero chỉ thu nhỏ & mờ dần, KHÔNG xoay/blur mạnh để tránh khó chịu
    const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.95, 0.8]);
    const opacity = useTransform(scrollYProgress, [0, 0.4, 0.8], [1, 0.8, 0]);
    const y = useTransform(scrollYProgress, [0, 1], [0, -100]);

    const smoothScale = useSpring(scale, { stiffness: 100, damping: 30 });
    const smoothY = useSpring(y, { stiffness: 100, damping: 30 });
    const smoothOpacity = useSpring(opacity, { stiffness: 100, damping: 30 });

    const motionStyles = isMobile
        ? {
            transformOrigin: "center top" as const,
        }
        : {
            scale: smoothScale,
            y: smoothY,
            opacity: smoothOpacity,
            transformOrigin: "center top" as const,
        };

    return (
        <div ref={ref} className="relative" style={{ perspective: 1200 }}>
            <motion.div
                style={motionStyles}
                className={className}
            >
                {children}
            </motion.div>
        </div>
    );
}
